import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import ProtectedLayout from "../layouts/ProtectedLayout";
import DefaultLayout from "../layouts/DefaultLayout";
import PublicLayout from "../layouts/PublicLayout";
// import BuilderLayout from "src/layouts/BuilderLayout";

import AuthHeader from "src/components/common/AuthHeader";
import BuilderHeader from "src/components/common/BuilderHeader";

//:- Pages to be rendered on specific endpoints. Lazy, like the portal below:
// eagerly imported, every page landed in one 3.9 MB / 922 KB-gz main bundle
// that the login page and every route had to download, parse and run —
// including ~1.2 MB of builder preview fixtures (config/dummy/widgets) that
// only the builder uses. Each page is now its own chunk, fetched on first
// visit; the Suspense below shows nothing for that moment, and
// ChunkErrorBoundary (App.tsx) handles a chunk that fails after a deploy.
const AnalyticsLogin = lazy(() => import("../pages/LoginPage"));
// Prefetched (webpack adds a <link rel="prefetch">): almost every login lands
// on a dashboard, so the browser downloads its chunk at idle priority while
// the login page is open — the dashboard is ready when "Sign in" succeeds,
// without putting it back in the main bundle the login page waits for.
const DashboardPage = lazy(
  () => import(/* webpackPrefetch: true */ "../pages/DashboardPage"),
);
const ReportPage = lazy(() => import("src/pages/ReportPage"));
const DashboardBuilder = lazy(() => import("src/pages/DashboardBuilder"));
const UserManagement = lazy(() => import("src/pages/UserManagement"));
const RecycleBinPage = lazy(() => import("src/pages/RecycleBinPage"));
const ChangePassword = lazy(() => import("src/pages/ChangePassword"));
const ForgotPassword = lazy(() => import("src/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("src/pages/ResetPassword"));

//:- Admin portal — lazy chunk, kept out of the analytics bundle
const PortalLogin = lazy(() => import("src/pages/portal/PortalLogin"));
const PortalLayout = lazy(() => import("src/layouts/PortalLayout"));
const PortalUsersPage = lazy(() => import("src/pages/portal/UsersPage"));
const PortalClientsPage = lazy(() => import("src/pages/portal/ClientsPage"));
const PortalProgramsPage = lazy(() => import("src/pages/portal/ProgramsPage"));
const PortalTiersPage = lazy(() => import("src/pages/portal/TiersPage"));
const PortalDashboardsPage = lazy(
  () => import("src/pages/portal/DashboardsPage"),
);
const PortalKpisPage = lazy(() => import("src/pages/portal/KpisPage"));
const PortalUsagePage = lazy(() => import("src/pages/portal/UsagePage"));
const PortalGuidePage = lazy(() => import("src/pages/portal/GuidePage"));

export default function AnalyticsRoutes() {
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          {/* All Protected Routes will be placed in this group */}
          <Route element={<ProtectedLayout header={<AuthHeader />} />}>
            <Route path="/reports/:report_name" element={<ReportPage />} />
            <Route
              path="/dashboards/:dashboard_name?"
              element={<DashboardPage />}
            />
            <Route path="/user-management" element={<UserManagement />} />
            <Route path="/recycle-bin" element={<RecycleBinPage />} />
            <Route path="/change-password" element={<ChangePassword />} />
          </Route>

          {/* All Protected Buider Routes will be placed in this group */}
          <Route element={<ProtectedLayout header={<BuilderHeader />} />}>
            <Route
              path="/builder/:dashboard_name?"
              element={<DashboardBuilder />}
            />
          </Route>

          <Route element={<PublicLayout />}>
            <Route path="/users/sign_in" element={<AnalyticsLogin />} />
            <Route path="/users/forgot_password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Admin portal — own login + role-gated shell */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route element={<PortalLayout />}>
            <Route
              path="/portal"
              element={<Navigate to="/portal/users" replace />}
            />
            <Route path="/portal/users" element={<PortalUsersPage />} />
            <Route path="/portal/clients" element={<PortalClientsPage />} />
            <Route path="/portal/programs" element={<PortalProgramsPage />} />
            <Route path="/portal/tiers" element={<PortalTiersPage />} />
            <Route
              path="/portal/dashboards"
              element={<PortalDashboardsPage />}
            />
            <Route path="/portal/kpis" element={<PortalKpisPage />} />
            {/* Usage is super_admin-only. The backend 403s regardless; the page
                also self-gates and the nav entry is hidden for other roles. */}
            <Route path="/portal/usage" element={<PortalUsagePage />} />
            {/* Guide is super_admin-only too: nav entry hidden for other roles,
                and the page self-gates (it calls no admin-only endpoint). */}
            <Route path="/portal/guide" element={<PortalGuidePage />} />
          </Route>

          <Route path="/" element={<DefaultLayout />} />
          <Route path="*" element={<DefaultLayout />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
